import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { performRequestMassAction } from '../../../../api'
import { EMPTY_OBJECT } from '../../../../constants/common'
import {
  useMassActions,
  useNotification,
  useRefetchCounters,
} from '../../../../hooks'
import {
  getItemsIds,
  getMassActionExcludeIdParam,
  getMassActionIdParam,
} from '../../../../utils/common'
import { getIdFilterParam } from '../../../../utils/filter'
import {
  FORMIK_ENTITY_ACTION_CONFIG_FIELDS_TYPE_SELECT_NAME,
  getFormikEntityActionConfigFieldsInitialValuesPayload,
} from '../../../Formik'
import { getRelatedRequestsLimitErrorMeta } from '../../AttachServiceRequestsModal'
import {
  REQUEST_MASS_ACTION_ID_KEY,
  REQUEST_MASS_ACTION_NAMES,
} from '../../Request.constants.actions'
import { getRequestMassActionUnreadFilter } from '../../Request.utils.actions'
import {
  getAsyncNotificationText,
  getNotificationTitle,
} from '../../Request.utils.notifications'

export function useRequestTableMassActionRequest({
  excludeIds,
  filterValues,
  pagination,
  selectAllPages,
  selectedIds,
  sub,
  onCleanSelection,
  onCloseModal,
  onReload,
}) {
  const refetchCounters = useRefetchCounters()

  const { t } = useTranslation()

  const [isPerformingMassAction, setPerformingMassAction] = useState(false)

  const { handleAddErrorNotification } = useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText,
    getNotificationTitle,
    onSuccessHandler: onReload,
  })

  function runMassAction({ action, countOfItems, performAction }) {
    onCloseModal()

    setPerformingMassAction(true)

    performAction()
      .then(({ data }) => {
        onCleanSelection()

        const asyncTask = getAsyncTaskId(data)

        if (asyncTask) {
          handleAddAsyncMassActionNotification({
            action,
            pathname: location.pathname,
            sub,
            task: asyncTask,
            totalCount: countOfItems,
          })

          return
        }

        onReload()

        handleAddFinishMassActionNotification(data, {
          action,
          totalCount: countOfItems,
        })

        refetchCounters()
      })
      .catch(error => {
        onCloseModal()

        const limitErrorMeta = getRelatedRequestsLimitErrorMeta(error)

        handleAddErrorNotification(
          limitErrorMeta
            ? {
                title: t(
                  'Modal.AttachServiceRequestsLimitExceededKey',
                  limitErrorMeta
                ),
              }
            : 'Notification.RequestMassActionErrorKey'
        )
      })
      .finally(() => {
        setPerformingMassAction(false)
      })
  }

  function massAction({ action, actionType, ids, value = EMPTY_OBJECT }) {
    const id = getMassActionIdParam({ ids, selectAllPages, selectedIds })
    const exclude_id = getMassActionExcludeIdParam({
      excludeIds,
      selectAllPages,
    })
    const unread = getRequestMassActionUnreadFilter(sub)

    const params = {
      ...getFormikEntityActionConfigFieldsInitialValuesPayload(actionType),
      action: REQUEST_MASS_ACTION_NAMES[action],
      all: selectAllPages,
      filters: {
        ...filterValues,
        [REQUEST_MASS_ACTION_ID_KEY]: getIdFilterParam({
          filterValues,
          ids: id,
          selectAllPages,
        }), // needs to prevent action with related request
        exclude_id,
        unread,
      },
      payload: value,
    }

    runMassAction({
      action,
      countOfItems: selectAllPages ? pagination.count : id.length,
      performAction: () => performRequestMassAction(params),
    })
  }

  async function handleAttachRequests({ action, ids, selected, status }) {
    massAction({
      action,
      ids,
      value: {
        ids: getItemsIds(selected),
        ...(status ? { restore_archived_with_status: status } : null),
      },
    })
  }

  function getHandleSubmitActionWithType({ action, id }) {
    return ({ payload, values }) => {
      massAction({
        ...(id ? { ids: [id] } : null),
        action,
        actionType: values[FORMIK_ENTITY_ACTION_CONFIG_FIELDS_TYPE_SELECT_NAME],
        value: payload,
      })
    }
  }

  return {
    getHandleSubmitActionWithType,
    handleAttachRequests,
    isPerformingMassAction,
    massAction,
    runMassAction,
  }
}
